import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { meProfileQueryOptions } from "@/lib/me-profile";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { humanizeApiError } from "@/lib/form-errors";
import { useToast } from "@/components/ui/Toast";
import { userProfilePatchSchema, type UserProfileResponse } from "@/lib/user-profile-schemas";
import { useTopbar } from "@/components/chrome/use-topbar";
import { AvatarUpload } from "@/components/ui/AvatarUpload";
import { US_STATES } from "@/lib/us-states";

export const Route = createFileRoute("/_app/settings/me")({
  component: SettingsMePage,
});

function SettingsMePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: profile, isLoading } = useQuery(meProfileQueryOptions);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [canSignMailers, setCanSignMailers] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // Hydrate local state from the fetched profile exactly once per user.
  useEffect(() => {
    if (!profile) return;
    if (loadedFor === profile.userId) return;
    setFirstName(profile.firstName ?? "");
    setLastName(profile.lastName ?? "");
    setTitle(profile.title ?? "");
    setCompany(profile.company ?? "");
    setCity(profile.city ?? "");
    setStateCode(profile.state ?? "");
    setContactPhone(profile.contactPhone ?? "");
    setContactEmail(profile.contactEmail ?? "");
    setCanSignMailers(profile.canSignMailers);
    setLoadedFor(profile.userId);
  }, [profile, loadedFor]);

  const save = useMutation({
    mutationFn: async () => {
      const body = userProfilePatchSchema.parse({
        firstName: firstName.trim() === "" ? null : firstName.trim(),
        lastName: lastName.trim() === "" ? null : lastName.trim(),
        title: title.trim() === "" ? null : title.trim(),
        company: company.trim() === "" ? null : company.trim(),
        city: city.trim() === "" ? null : city.trim(),
        state: stateCode.trim() === "" ? null : stateCode.trim(),
        contactPhone: contactPhone.trim() === "" ? null : contactPhone.trim(),
        contactEmail: contactEmail.trim() === "" ? null : contactEmail.trim(),
        canSignMailers,
      });
      return apiFetch<UserProfileResponse>("/api/me/profile", {
        method: "PUT",
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      toast.success("Profile saved.");
      void qc.invalidateQueries({ queryKey: ["me", "profile"] });
      void qc.invalidateQueries({ queryKey: ["sign-as-roster"] });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 401) {
        void navigate({ to: `/login?redirect=${encodeURIComponent(pathname)}` });
        return;
      }
      toast.error(humanizeApiError(err).summary);
    },
  });

  const saveError = save.isError ? humanizeApiError(save.error) : null;

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "My profile" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  if (isLoading || !profile) {
    return <p className="text-sm text-muted">Loading profile…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">My profile</h1>
        <p className="text-sm text-muted">
          Shown on borrower-facing PDFs (when you&apos;re set as a signer) and in the app sidebar.
        </p>
      </div>

      <form
        className="grid max-w-xl gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <AvatarUpload
          currentUrl={profile.avatarUrl}
          displayName={`${firstName} ${lastName}`.trim() || profile.displayName}
          uploadPath="/api/me/avatar"
          removePath="/api/me/avatar"
          allowRemove
          onChanged={() => void qc.invalidateQueries({ queryKey: ["me", "profile"] })}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">
              First name
            </span>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
            {saveError?.fields.firstName ? (
              <span className="text-xs text-neg">{saveError.fields.firstName}</span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">
              Last name
            </span>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
            {saveError?.fields.lastName ? (
              <span className="text-xs text-neg">{saveError.fields.lastName}</span>
            ) : null}
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">
            Title (shown on borrower PDFs)
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Loan Origination"
            className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
          {saveError?.fields.title ? (
            <span className="text-xs text-neg">{saveError.fields.title}</span>
          ) : null}
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">
            Company (shown on borrower PDFs)
          </span>
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Dynamo Capital"
            className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
          {saveError?.fields.company ? (
            <span className="text-xs text-neg">{saveError.fields.company}</span>
          ) : null}
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">City</span>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
            {saveError?.fields.city ? (
              <span className="text-xs text-neg">{saveError.fields.city}</span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted">State</span>
            <select
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            >
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code}
                </option>
              ))}
            </select>
            {saveError?.fields.state ? (
              <span className="text-xs text-neg">{saveError.fields.state}</span>
            ) : null}
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">
            Contact phone (mailer)
          </span>
          <input
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="720-555-0117"
            className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
          {saveError?.fields.contactPhone ? (
            <span className="text-xs text-neg">{saveError.fields.contactPhone}</span>
          ) : null}
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted">
            Contact email override
          </span>
          <input
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder={profile.contactEmail ?? ""}
            className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
          {saveError?.fields.contactEmail ? (
            <span className="text-xs text-neg">{saveError.fields.contactEmail}</span>
          ) : null}
        </label>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={canSignMailers}
            onChange={(e) => setCanSignMailers(e.target.checked)}
            className="h-4 w-4"
          />
          <span className="text-sm text-ink">
            Eligible to sign borrower mailers (&ldquo;Sign as&rdquo; roster)
          </span>
        </label>
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-gold px-4 py-2 text-sm font-semibold text-ink disabled:opacity-60"
          >
            {save.isPending ? "Saving…" : "Save profile"}
          </button>
          {saveError ? <span className="text-xs text-neg">{saveError.summary}</span> : null}
          {save.isSuccess && !save.isPending ? (
            <span className="text-xs text-pos">Saved.</span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
